if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.thumbnailObserver = null;
      }

      hide() {
        super.hide();
      }

      show(opener) {
        this.configureResponsiveImages();
        this.setupNavigation();
        super.show(opener);
        this.showActiveMedia();
      }

      configureResponsiveImages() {
        const useMobileImages = window.matchMedia('(max-width: 749px)').matches;

        this.getMediaItems().forEach((media) => {
          const image = media.matches('img') ? media : media.querySelector('img');
          if (!image) return;

          if (!image.dataset.modalOriginalSrc) {
            image.dataset.modalOriginalSrc = image.getAttribute('src') || '';
            image.dataset.modalOriginalSrcset = image.getAttribute('srcset') || '';
            image.dataset.modalOriginalSizes = image.getAttribute('sizes') || '';
          }

          image.decoding = 'async';

          if (useMobileImages) {
            const source = image.dataset.modalOriginalSrc || image.src;
            const mobile550 = this.getSizedImageSource(source, 550);
            const mobile750 = this.getSizedImageSource(source, 750);
            image.srcset = `${mobile550} 550w, ${mobile750} 750w`;
            image.sizes = 'calc(100vw - 8.2rem)';
            image.src = mobile750;
          } else {
            image.src = image.dataset.modalOriginalSrc;
            if (image.dataset.modalOriginalSrcset) image.srcset = image.dataset.modalOriginalSrcset;
            else image.removeAttribute('srcset');
            if (image.dataset.modalOriginalSizes) image.sizes = image.dataset.modalOriginalSizes;
            else image.removeAttribute('sizes');
          }
        });
      }

      setupNavigation() {
        const dialog = this.querySelector('.product-media-modal__dialog');
        const content = this.querySelector('.product-media-modal__content');
        if (!dialog || !content) return;

        const mediaItems = this.getMediaItems();
        const navigationSignature = mediaItems.map((media) => media.dataset.mediaId).join('|');
        if (
          this.dataset.navigationSignature === navigationSignature &&
          this.querySelector('.product-media-modal__thumbnails')
        ) {
          return;
        }

        this.thumbnailObserver?.disconnect();
        this.querySelector('.product-media-modal__thumbnails')?.remove();
        this.querySelector('.product-media-modal__counter')?.remove();
        this.dataset.navigationSignature = navigationSignature;

        const thumbnailRail = document.createElement('div');
        thumbnailRail.className = 'product-media-modal__thumbnails';
        thumbnailRail.setAttribute('aria-label', 'Seleziona immagine');
        thumbnailRail.hidden = mediaItems.length < 2;
        thumbnailRail.addEventListener('pointerup', (event) => event.stopPropagation());
        thumbnailRail.addEventListener('click', (event) => event.stopPropagation());

        mediaItems.forEach((media, index) => {
          const previewImage = media.matches('img') ? media : media.querySelector('img');
          if (!previewImage) return;

          const thumbnailButton = document.createElement('button');
          thumbnailButton.type = 'button';
          thumbnailButton.className = 'product-media-modal__thumbnail';
          thumbnailButton.dataset.mediaId = media.dataset.mediaId;
          thumbnailButton.setAttribute('aria-label', `Visualizza immagine ${index + 1}`);

          const thumbnailImage = document.createElement('img');
          thumbnailImage.dataset.src = this.getThumbnailSource(previewImage);
          thumbnailImage.alt = previewImage.alt || '';
          thumbnailImage.loading = 'lazy';
          thumbnailImage.decoding = 'async';
          thumbnailImage.width = 96;
          thumbnailImage.height = 96;
          thumbnailButton.append(thumbnailImage);

          if (!media.matches('img')) {
            const mediaBadge = document.createElement('span');
            mediaBadge.className = 'product-media-modal__thumbnail-badge';
            mediaBadge.setAttribute('aria-hidden', 'true');
            mediaBadge.textContent = '▶';
            thumbnailButton.append(mediaBadge);
          }

          thumbnailButton.addEventListener('click', () => {
            const activeIndex = Math.max(
              0,
              mediaItems.findIndex((item) => item.classList.contains('active'))
            );
            this.activateMedia(media, true, index >= activeIndex ? 'next' : 'previous');
          });
          thumbnailRail.append(thumbnailButton);
        });

        const counter = document.createElement('div');
        counter.className = 'product-media-modal__counter';
        counter.setAttribute('aria-live', 'polite');

        dialog.append(thumbnailRail, counter);

        const loadThumbnail = (image) => {
          if (!image?.dataset.src) return;
          image.src = image.dataset.src;
          delete image.dataset.src;
        };

        if ('IntersectionObserver' in window) {
          this.thumbnailObserver = new IntersectionObserver(
            (entries, observer) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                loadThumbnail(entry.target);
                observer.unobserve(entry.target);
              });
            },
            { root: thumbnailRail, rootMargin: '120px 0px' }
          );
          thumbnailRail.querySelectorAll('img[data-src]').forEach((image) => this.thumbnailObserver.observe(image));
        } else {
          thumbnailRail.querySelectorAll('img[data-src]').forEach(loadThumbnail);
        }

        if (content.dataset.touchNavigationReady !== 'true') {
          content.dataset.touchNavigationReady = 'true';
          content.addEventListener(
            'touchstart',
            (event) => {
              this.touchStartX = event.changedTouches[0].screenX;
              this.touchStartY = event.changedTouches[0].screenY;
            },
            { passive: true }
          );

          content.addEventListener(
            'touchend',
            (event) => {
              const deltaX = event.changedTouches[0].screenX - this.touchStartX;
              const deltaY = event.changedTouches[0].screenY - this.touchStartY;

              if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
                this.changeMedia(deltaX < 0 ? 1 : -1);
              }
            },
            { passive: true }
          );
        }

        if (this.dataset.keyboardNavigationReady !== 'true') {
          this.dataset.keyboardNavigationReady = 'true';
          this.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowLeft') this.changeMedia(-1);
            if (event.key === 'ArrowRight') this.changeMedia(1);
          });
        }
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      getThumbnailSource(image) {
        const source = image.currentSrc || image.src;
        if (!source) return '';

        try {
          const thumbnailUrl = new URL(source, window.location.origin);
          thumbnailUrl.searchParams.set('width', '160');
          return thumbnailUrl.toString();
        } catch (error) {
          return source;
        }
      }

      getSizedImageSource(source, width) {
        if (!source) return '';

        try {
          const imageUrl = new URL(source, window.location.origin);
          imageUrl.searchParams.set('width', String(width));
          return imageUrl.toString();
        } catch (error) {
          return source;
        }
      }

      changeMedia(offset) {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        const activeIndex = Math.max(
          0,
          mediaItems.findIndex((media) => media.classList.contains('active'))
        );
        const nextIndex = (activeIndex + offset + mediaItems.length) % mediaItems.length;
        this.activateMedia(mediaItems[nextIndex], true, offset > 0 ? 'next' : 'previous');
      }

      activateMedia(activeMedia, smooth = false, direction = null) {
        const mediaItems = this.getMediaItems();
        mediaItems.forEach((media) => {
          media.classList.remove(
            'active',
            'product-media-modal__item--enter-next',
            'product-media-modal__item--enter-previous'
          );
        });
        activeMedia.classList.add('active');
        this.preloadAdjacentMedia(activeMedia);

        if (
          direction &&
          window.matchMedia('(max-width: 749px)').matches &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          const animationClass =
            direction === 'next'
              ? 'product-media-modal__item--enter-next'
              : 'product-media-modal__item--enter-previous';
          activeMedia.classList.add(animationClass);
          activeMedia.addEventListener('animationend', () => activeMedia.classList.remove(animationClass), {
            once: true,
          });
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }

        this.updateMobileNavigation();
      }

      preloadAdjacentMedia(activeMedia) {
        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) return;

        const activeIndex = Math.max(0, mediaItems.indexOf(activeMedia));
        const indexes = [activeIndex, (activeIndex + 1) % mediaItems.length, (activeIndex - 1 + mediaItems.length) % mediaItems.length];

        indexes.forEach((index) => {
          const media = mediaItems[index];
          const image = media?.matches('img') ? media : media?.querySelector('img');
          if (!image) return;
          image.loading = 'eager';
          image.fetchPriority = index === activeIndex ? 'high' : 'low';
          image.decode?.().catch(() => {});
        });
      }

      updateMobileNavigation() {
        const mediaItems = this.getMediaItems();
        const activeIndex = Math.max(
          0,
          mediaItems.findIndex((media) => media.classList.contains('active'))
        );
        const hasMultipleMedia = mediaItems.length > 1;

        const counter = this.querySelector('.product-media-modal__counter');
        if (counter) {
          counter.hidden = !hasMultipleMedia;
          counter.textContent = hasMultipleMedia ? `${activeIndex + 1} / ${mediaItems.length}` : '';
        }

        const activeMediaId = mediaItems[activeIndex]?.dataset.mediaId;
        this.querySelectorAll('.product-media-modal__thumbnail').forEach((thumbnail) => {
          const isActive = thumbnail.dataset.mediaId === activeMediaId;
          thumbnail.classList.toggle('is-active', isActive);
          if (isActive) {
            thumbnail.setAttribute('aria-current', 'true');
            const rail = thumbnail.closest('.product-media-modal__thumbnails');
            if (rail) {
              const railRect = rail.getBoundingClientRect();
              const thumbnailRect = thumbnail.getBoundingClientRect();
              if (thumbnailRect.top < railRect.top || thumbnailRect.bottom > railRect.bottom) {
                thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
              }
            }
          } else {
            thumbnail.removeAttribute('aria-current');
          }
        });
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (activeMedia) this.activateMedia(activeMedia);
      }
    }
  );
}
